import React from 'react';
import { array, boolean, object, string, ValidationError } from 'yup';

import {
  requiredNumberType,
  stringType,
  requiredStringType,
  requiredAutocompleteType,
  textType,
  requiredTextType,
  dateType,
  requiredDateType
} from './validationTypes';

const employerType = object().shape({
  name: requiredAutocompleteType.test({
    test: function() {
      return !this.parent.isFake;
    },
    message:
      'SumZero members disclose their employer names. Fake firm names are not permitted. Please contact access@sumzero.com with any questions.'
  }),
  fundType: requiredStringType,
  aumRange: stringType
});

const employerHistoryType = object().shape({
  currentEmployer: boolean(),
  employer: employerType,
  fromDate: dateType,
  primary: boolean(),
  title: stringType,
  toDate: dateType.max(new Date(), 'Select "I Am Currently Employed Here" for current positions.')
});

const schoolHistoryType = object().shape({
  schoolName: stringType.ensure().required(
    <>
      Please contact{' '}
      <a style={{ color: '#4a90e2' }} href="mailto:support@sumzero.com">
        support@sumzero.com
      </a>{' '}
      if your educational institution did not appear in the list above.
    </>
  ),
  degree: stringType,
  fieldOfStudy: stringType,
  startYear: stringType,
  gradYear: stringType
});

const profileTestResultType = object().shape({
  name: requiredStringType,
  score: requiredNumberType,
  maxScore: string().when('name', {
    is: name => ['SAT', 'GPA'].includes(name),
    then: requiredStringType,
    otherwise: stringType
  }),
  date: requiredDateType,
  filename: stringType
});

const profileAttachmentType = object().shape({
  name: requiredStringType,
  filename: requiredStringType,
  description: requiredTextType
});

const profileAnswerType = object().shape({
  answer: textType
});

export const cardSchema = object().shape({
  userProfile: object().shape({
    city: requiredAutocompleteType,
    summary: textType.max(1250, 'Summary cannot be greater than 1,250 characters')
  })
});

export const workExperienceSchema = object().shape({
  userProfile: object().shape({
    employerHistories: array()
      .of(employerHistoryType)
      .test({
        test: function(employerHistories) {
          const {
            currentEmployersIndices,
            currentPrimaryEmployersIndices
          } = employerHistories.reduce(
            (acc, employer, idx) => {
              if (employer.currentEmployer) {
                acc.currentEmployersIndices.push(idx);
                if (employer.primary) acc.currentPrimaryEmployersIndices.push(idx);
              }
              return acc;
            },
            { currentEmployersIndices: [], currentPrimaryEmployersIndices: [] }
          );

          if (currentEmployersIndices.length > 1 && currentPrimaryEmployersIndices.length !== 1) {
            const errors = currentEmployersIndices.map(index => {
              return new ValidationError(
                'Please designate one current employer as primary when multiple are listed.',
                employerHistories[index],
                `${this.path}[${index}].primary`
              );
            });

            return new ValidationError(errors);
          }

          return true;
        }
      })
  })
});

export const educationSchema = object().shape({
  userProfile: object().shape({
    schoolHistories: array().of(schoolHistoryType)
  })
});

export const personalDetailsSchema = object().shape({
  userProfile: object().shape({
    hometown: stringType,
    investmentHero: stringType,
    favoriteInvestmentBook: stringType,
    investingStyle: stringType,
    languagesSpoken: stringType
  })
});

export const resumePhoneSchema = object().shape({
  userProfile: object().shape({
    workPhone: stringType
  })
});

export const academicPerformanceSchema = object().shape({
  userProfile: object().shape({
    profileTestResults: array().of(profileTestResultType)
  })
});

export const researchSchema = object().shape({
  userProfile: object().shape({
    profileAttachments: array().of(profileAttachmentType)
  })
});

export const qAndASchema = object().shape({
  userProfile: object().shape({
    profileAnswers: array().of(profileAnswerType)
  })
});
